import React, { FC, useEffect, useState } from 'react';
import { Button, ButtonMode } from '@components/atoms/Button';
import { Typography, TypographyTagVariants } from '@components/atoms/Typography';
import { useNotifications } from '@hooks/useNotifications';
import { useTranslation } from 'react-i18next';
import { Popup } from '@components/atoms/Popup';
import { useCreateSuggestion } from '@services/useCreateSuggestion';
import styles from './SuggestChangesModal.module.css';
import { useData } from '@hooks/useData';
import { RadioButton, RadioButtonType } from '@components/atoms/RadioButton';
import { Select, SelectType, SimpleOptionsList } from '@components/atoms/Select';
import { Space } from '@components/atoms/Space';
import { ErrorMessages } from '@components/atoms/ErrorMessages';

export type Answer = string;

export type AnswerState = { [key: string]: Answer };

export interface Question {
    question: string;
    type: 'boolean' | 'text' | 'textarea' | 'select';
    options_source?: 'parent_categories' | 'all_categories' | 'attributes';
    answers?: {
        yes?: Question[];
        no?: Question[];
        [key: string]: Question[] | undefined;
    };
}

export interface SuggestChangesModalProps {
    type: 'product-categories' | 'service-categories' | 'attributes';
    dataForSelects: QuestionComponentProps['dataForSelects'];
    show: boolean;
    onClose: () => void;
}

export const SuggestChangesModal: FC<SuggestChangesModalProps> = ({ type, dataForSelects, show, onClose }) => {
    const { showDefaultNotification } = useNotifications();
    const {
        request: requestCreateSuggestion,
        loading: loadingCreateSuggestion,
        success: successCreateSuggestion,
        errorMessage: errorCreateSuggestion,
        lastResponseTime: lastCreateSuggestion,
    } = useCreateSuggestion();
    const { allSuggestionQuestions } = useData();
    const { t } = useTranslation();

    const translations = {
        title: t('suggest-changes-modal-component.title'),
        sendButton: t('suggest-changes-modal-component.send.button'),
        cancelButton: t('suggest-changes-modal-component.cancel.button'),
        requiredAnswersError: t('suggest-changes-modal-component.required.error'),
        yes: t('suggest-changes-modal-component.yes.label'),
        no: t('suggest-changes-modal-component.no.label'),
    };

    const [answer, setAnswer] = useState<AnswerState>({});
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (lastCreateSuggestion) {
            if (errorCreateSuggestion) {
                setError(errorCreateSuggestion);
            } else {
                showDefaultNotification({ type: 'success', message: successCreateSuggestion || '' });
                // Reset form
                setAnswer({});
                setError(null);
                onClose();
            }
        }
    }, [lastCreateSuggestion]);

    // Helper function to get all visible questions recursively
    const getAllVisibleQuestions = (questions: Question[], currentAnswers: AnswerState): Question[] => {
        let allQuestions: Question[] = [];

        questions.forEach((question) => {
            allQuestions.push(question);

            const answerValue = currentAnswers[question.question];
            let answerKey: string | undefined;

            if (answerValue === 'yes' || answerValue === 'no') {
                answerKey = answerValue;
            } else if (typeof answerValue === 'string' && answerValue !== '') {
                answerKey = answerValue;
            }

            if (answerKey && question.answers && question.answers[answerKey]) {
                allQuestions = [...allQuestions, ...getAllVisibleQuestions(question.answers[answerKey] || [], currentAnswers)];
            }
        });

        return allQuestions;
    };

    const validateAnswers = (): boolean => {
        const visibleQuestions = getAllVisibleQuestions((allSuggestionQuestions?.data?.[type] as Question[]) || [], answer);

        for (const question of visibleQuestions) {
            const answerValue = answer[question.question];
            if (answerValue === undefined || answerValue === '') {
                return false;
            }
        }

        return true;
    };

    const handleAnswerChange = (updater: React.SetStateAction<AnswerState>) => {
        setAnswer(updater);
        setError(null);
    };

    const handleOnClickSuggest = () => {
        if (!validateAnswers()) {
            setError(translations.requiredAnswersError);
            return;
        }

        setError(null);

        // Transform answers for submission - convert yes/no to display labels
        const transformedAnswer: AnswerState = {};
        Object.keys(answer).forEach((key) => {
            const value = answer[key];
            if (value === 'yes') {
                transformedAnswer[key] = translations.yes;
            } else if (value === 'no') {
                transformedAnswer[key] = translations.no;
            } else {
                transformedAnswer[key] = value;
            }
        });

        requestCreateSuggestion({ suggestion: transformedAnswer });
    };

    const handleClose = () => {
        // Reset form when closing
        setAnswer({});
        setError(null);
        onClose();
    };

    return (
        <Popup
            show={show}
            onHide={handleClose}
            desktopWidth="1220px"
            fullWidthOnMobile={true}
            header={
                <>
                    <div className={styles.header}>
                        <Typography tagVariant={TypographyTagVariants.h5} fontWeight="900" fontSize="20">
                            {translations.title}
                        </Typography>
                    </div>
                    <hr className={styles.divider} />
                </>
            }>
            <div className={styles.content}>
                <div className="d-flex flex-column gap-3">
                    <>
                        {allSuggestionQuestions?.data?.[type]?.map((item, index) => (
                            <QuestionComponent
                                key={`question-${index}`}
                                question={item as Question}
                                answer={answer}
                                setAnswer={handleAnswerChange}
                                dataForSelects={dataForSelects}
                            />
                        ))}
                    </>

                    <div className="d-flex gap-3 justify-content-between">
                        <div>
                            {error && (
                                <>
                                    <ErrorMessages messages={[error]} />
                                    <Space spaceBreakpoints={{ xs: { space: 15 } }} />
                                </>
                            )}
                        </div>
                        <div className="d-flex gap-3 align-self-end">
                            <Button
                                label={translations.cancelButton}
                                mode={ButtonMode.danger}
                                onClick={handleClose}
                                disabled={loadingCreateSuggestion}
                            />
                            <Button
                                label={translations.sendButton}
                                mode={ButtonMode.primary}
                                onClick={handleOnClickSuggest}
                                disabled={loadingCreateSuggestion}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Popup>
    );
};

interface QuestionComponentProps {
    question: Question;
    answer: AnswerState;
    setAnswer: (updater: React.SetStateAction<AnswerState>) => void;
    dataForSelects: {
        parentCategories?: string[];
        allCategories?: string[];
        attributes?: string[];
    };
}

export const QuestionComponent: React.FC<QuestionComponentProps> = ({ question, answer, setAnswer, dataForSelects }) => {
    const { t } = useTranslation();

    const translations = {
        yes: t('suggest-changes-modal-component.yes.label'),
        no: t('suggest-changes-modal-component.no.label'),
        defaultOption: t('select-default-option'),
    };

    const questionKey = question.question;
    const selectedValue = answer[questionKey];

    const handleBooleanChange = (value: 'yes' | 'no') => {
        setAnswer((prev) => ({
            ...prev,
            [questionKey]: value,
        }));
    };

    const handleTextChange = (value: string) => {
        setAnswer((prev) => ({
            ...prev,
            [questionKey]: value,
        }));
    };

    // Map value to answer key for nested question lookup
    const getAnswerKey = (): string | undefined => {
        if (selectedValue === 'yes' || selectedValue === 'no') {
            return selectedValue;
        }
        if (typeof selectedValue === 'string' && selectedValue !== '') {
            return selectedValue;
        }
        return undefined;
    };

    // Get select options based on options_source
    const getSelectOptions: () => SimpleOptionsList = () => {
        const defaultOption = { value: '', text: translations.defaultOption };

        if (question.type !== 'select' || !question.options_source) {
            return [defaultOption];
        }

        let options: string[] = [];

        // Determine which data to use based on options_source
        if (question.options_source === 'parent_categories') {
            options = dataForSelects.parentCategories || [];
        } else if (question.options_source === 'all_categories') {
            options = dataForSelects.allCategories || [];
        } else if (question.options_source === 'attributes') {
            options = dataForSelects.attributes || [];
        }

        return [
            defaultOption,
            ...options.map((option) => ({
                value: option,
                text: option,
            })),
        ];
    };

    const answerKey = getAnswerKey();
    const nestedQuestions = answerKey && question.answers ? question.answers[answerKey] : undefined;

    return (
        <div className="mb-3">
            {/* question text */}
            <Typography fontWeight="800" tagVariant={TypographyTagVariants.span}>
                <>
                    {question.question} <span className="text-danger">*</span>
                </>
            </Typography>

            {/* boolean input */}
            {question.type === 'boolean' && (
                <>
                    <div className="d-flex gap-4 pt-2">
                        <RadioButton
                            id={`${questionKey}-yes`}
                            type={RadioButtonType.normal}
                            name={questionKey}
                            value="yes"
                            label={translations.yes}
                            checked={selectedValue === 'yes'}
                            onChange={() => handleBooleanChange('yes')}
                        />
                        <RadioButton
                            id={`${questionKey}-no`}
                            type={RadioButtonType.normal}
                            name={questionKey}
                            value="no"
                            label={translations.no}
                            checked={selectedValue === 'no'}
                            onChange={() => handleBooleanChange('no')}
                        />
                    </div>
                </>
            )}

            {/* text input */}
            {question.type === 'text' && (
                <input
                    type="text"
                    className="form-control mt-2"
                    value={typeof selectedValue === 'string' ? selectedValue : ''}
                    onChange={(e) => handleTextChange(e.target.value)}
                    required
                />
            )}

            {/* textarea input */}
            {question.type === 'textarea' && (
                <textarea
                    className="form-control mt-2"
                    value={typeof selectedValue === 'string' ? selectedValue : ''}
                    onChange={(e) => handleTextChange(e.target.value)}
                    rows={3}
                    required
                />
            )}

            {/* select input */}
            {question.type === 'select' && (
                <Select
                    classes="w-100 mt-2"
                    type={SelectType.variant1}
                    options={getSelectOptions()}
                    value={typeof selectedValue === 'string' ? selectedValue : ''}
                    onChange={(value) => handleTextChange(value)}
                />
            )}

            {/* nested questions */}
            {nestedQuestions && nestedQuestions.length > 0 && (
                <div className="mt-3">
                    {nestedQuestions.map((nestedQuestion, index) => (
                        <QuestionComponent
                            key={`${questionKey}-${answerKey}-${index}`}
                            question={nestedQuestion}
                            answer={answer}
                            setAnswer={setAnswer}
                            dataForSelects={dataForSelects}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
